import { useEffect, useRef } from "react";

export default function HeroSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    el.style.opacity = "0";
    el.style.transform = "translateY(24px)";
    setTimeout(() => {
      el.style.transition = "opacity 0.9s ease, transform 0.9s ease";
      el.style.opacity = "1";
      el.style.transform = "translateY(0)";
    }, 100);
  }, []);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="home"
      className="relative min-h-screen flex flex-col justify-center overflow-hidden"
      style={{ background: "linear-gradient(135deg, #0a0a0a 0%, #111111 50%, #0d0d0d 100%)" }}
    >
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=abstract%20dark%20corporate%20digital%20marketing%20strategy%20background%20with%20subtle%20golden%20geometric%20patterns%20glowing%20lines%20premium%20elegant%20professional%20dark%20navy%20black%20texture%20minimal&width=1920&height=1080&seq=hero-bg-1&orientation=landscape"
          alt="Imperial Digital background"
          className="w-full h-full object-cover object-top opacity-25"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a]/70 via-[#0a0a0a]/50 to-[#0a0a0a]/90"></div>
      </div>

      {/* Decorative Lines */}
      <div className="absolute top-0 left-0 w-px h-full bg-gradient-to-b from-transparent via-[#C9A227]/20 to-transparent"></div>
      <div className="absolute top-0 right-0 w-px h-full bg-gradient-to-b from-transparent via-[#C9A227]/10 to-transparent"></div>

      {/* Content */}
      <div ref={sectionRef} className="relative z-10 max-w-7xl mx-auto px-8 lg:px-16 pt-32 pb-24">
        <div className="grid lg:grid-cols-2 gap-16 items-center">

          {/* Left Column */}
          <div>
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-[#1a1a1a] border border-[#C9A227]/30 rounded-full px-4 py-2 mb-8">
              <span className="w-2 h-2 rounded-full bg-[#C9A227] flex-shrink-0"></span>
              <span className="text-[#C9A227] text-xs font-semibold tracking-widest uppercase" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                Consultoría Premium con IA
              </span>
            </div>

            {/* Main Headline */}
            <h1
              className="text-white text-4xl lg:text-6xl font-bold leading-tight mb-6"
              style={{ fontFamily: "'Cormorant Garamond', serif", lineHeight: "1.15" }}
            >
              Estrategias de marketing que{" "}
              <span
                className="text-[#C9A227]"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                impulsan marcas
              </span>{" "}
              al siguiente nivel
            </h1>

            {/* Subtitle */}
            <p
              className="text-gray-400 text-lg mb-10 leading-relaxed max-w-xl"
              style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}
            >
              Soluciones de marketing digital personalizadas, impulsadas por inteligencia artificial, diseñadas para transformar tu visión en resultados medibles.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={() => scrollTo("contacto")}
                className="bg-[#C9A227] hover:bg-[#b8911e] text-black font-bold px-10 py-4 rounded-md transition-all duration-300 whitespace-nowrap cursor-pointer text-sm tracking-wide"
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              >
                Solicitar información
              </button>
              <button
                onClick={() => scrollTo("servicios")}
                className="border border-[#C9A227] text-[#C9A227] hover:bg-[#C9A227]/10 font-semibold px-10 py-4 rounded-md transition-all duration-300 whitespace-nowrap cursor-pointer text-sm tracking-wide"
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              >
                Ver servicios
              </button>
            </div>

            {/* Stats */}
            <div className="flex gap-10 mt-14 pt-10 border-t border-[#1f1f1f]">
              {[
                { number: "+150", label: "Clientes satisfechos" },
                { number: "+8", label: "Años de experiencia" },
                { number: "98%", label: "Tasa de retención" },
              ].map((stat) => (
                <div key={stat.label}>
                  <div
                    className="text-[#C9A227] text-2xl font-bold"
                    style={{ fontFamily: "'Cormorant Garamond', serif" }}
                  >
                    {stat.number}
                  </div>
                  <div
                    className="text-gray-500 text-xs mt-1 tracking-wide"
                    style={{ fontFamily: "'Montserrat', sans-serif" }}
                  >
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right Column */}
          <div className="relative hidden lg:block">
            {/* Main Image */}
            <div className="relative rounded-2xl overflow-hidden" style={{ height: "500px" }}>
              <img
                src="https://readdy.ai/api/search-image?query=professional%20business%20team%20smiling%20confidently%20in%20modern%20dark%20corporate%20office%20no%20masks%20no%20face%20coverings%20elegant%20men%20and%20women%20in%20suits%20discussing%20marketing%20strategy%20around%20table%20with%20digital%20screens%20dark%20luxury%20premium%20environment%20golden%20lighting&width=700&height=500&seq=hero-right-2&orientation=portrait"
                alt="Imperial Digital team"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-transparent to-transparent"></div>
              <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a]/30 to-transparent"></div>
            </div>

            {/* Floating Card */}
            <div className="absolute -bottom-6 -left-8 bg-[#111111] border border-[#C9A227]/30 rounded-xl p-5 shadow-2xl">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#C9A227]/10 border border-[#C9A227]/30">
                  <i className="ri-bar-chart-line text-[#C9A227] text-lg"></i>
                </div>
                <div>
                  <div className="text-white text-sm font-semibold" style={{ fontFamily: "'Montserrat', sans-serif" }}>Resultados demostrados</div>
                  <div className="text-gray-500 text-xs mt-0.5" style={{ fontFamily: "'Montserrat', sans-serif" }}>Crecimiento promedio del 230%</div>
                </div>
              </div>
            </div>

            {/* Top floating badge */}
            <div className="absolute top-6 -right-4 bg-[#C9A227] text-black rounded-xl p-4 shadow-2xl">
              <div className="text-2xl font-bold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>IA</div>
              <div className="text-xs font-semibold mt-0.5" style={{ fontFamily: "'Montserrat', sans-serif" }}>Powered</div>
            </div>
          </div>
        </div>

        {/* Bottom Hero Image */}
        <div className="mt-20 relative rounded-2xl overflow-hidden" style={{ height: "260px" }}>
          <img
            src="https://readdy.ai/api/search-image?query=dark%20luxury%20corporate%20digital%20marketing%20office%20space%20wide%20panoramic%20view%20with%20golden%20light%20accents%20modern%20furniture%20technology%20screens%20sophisticated%20premium%20atmosphere%20monochromatic&width=1400&height=260&seq=hero-bottom-1&orientation=landscape"
            alt="Imperial Digital workspace"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a]/70 via-transparent to-[#0a0a0a]/70"></div>
          <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#0a0a0a]/60"></div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
        <span className="text-gray-600 text-xs tracking-widest uppercase" style={{ fontFamily: "'Montserrat', sans-serif" }}>Scroll</span>
        <i className="ri-arrow-down-line text-[#C9A227]/60 text-lg"></i>
      </div>
    </section>
  );
}
